/**
 * 11.3 — recuperar a senha do Portal (achado PORTAL-01, adiado na execução anterior).
 *
 * ## Por que é um arquivo, e não mais dois ramos dentro do login
 *
 * O cartão de login já carregava dois modos (entrar e aceitar convite) e um ternário
 * em cada campo. Somar "esqueci" e "redefinir" ali daria quatro modos disputando o
 * mesmo JSX — o formato que o `main.tsx` deste portal foi quebrado justamente para
 * não ter. A spec pede o MESMO CARTÃO, não o mesmo componente: visualmente é a mesma
 * moldura, e é o que este arquivo desenha.
 *
 * ## A confirmação é NEUTRA, e é a spec quem exige
 *
 * O protótipo dizia "Enviamos um link para {email}". A spec 11.3 §Migração (item 5)
 * mata esse texto por nome: confirmar o endereço transforma a tela num oráculo de
 * enumeração — quem quisesse saber se a agência atende a Empresa X descobriria
 * digitando o e-mail de lá. O texto neutro é o do Portal, e não o do login interno,
 * que diz outra coisa.
 *
 * ## O link morto não cai no formulário em silêncio
 *
 * Um token vencido, já usado ou revogado devolve um estado NOMEADO com "Pedir novo
 * link". Sem isso, a pessoa digita a senha duas vezes, recebe um erro genérico e não
 * tem ideia de que o caminho é pedir outro e-mail.
 *
 * O servidor não tem rota para CONFERIR o token antes (o convite tem, porque precisa
 * mostrar de quem ele é). Aqui não haveria o que mostrar: a validade só se descobre
 * no envio, e o custo de descobrir ali é uma tentativa — contra o custo de mais uma
 * rota anônima de força bruta, que é permanente.
 */
import { useState } from 'react'
import { ArrowLeft, ArrowRight, Eye, EyeOff } from 'lucide-react'
import { euExternoDoPortal, MINIMO_DA_SENHA, type EuExternoDoPortal } from '@dv/contracts'
import { z } from 'zod'
import { enviar } from '../api/cliente.js'
import { FalhaDoPortal } from '../api/erros.js'

/** O contrato da resposta neutra: `{ok:true}` e nada mais que possa variar. */
const respostaNeutraDoPortal = z.object({ ok: z.literal(true) })

/** O token da redefinição vem na URL, no mesmo cartão (spec 11.3, linha 62). */
export function resetDaUrl(): string | null {
  return new URLSearchParams(location.search).get('reset')
}

/**
 * Tira da barra de endereço os DOIS parâmetros que escolhem um modo.
 *
 * Três razões, e a terceira só apareceu na revisão adversarial:
 *
 *   · o token não fica no histórico do navegador depois de usado;
 *   · um "Pedir novo link" que deixasse `reset` voltaria ao modo redefinir na
 *     navegação seguinte, com o token que acabou de ser recusado;
 *   · o `convite` NUNCA saía. Como `Login` deriva o modo de `conviteDaUrl()` a cada
 *     render, qualquer volta ao login com `?convite=` ainda na barra desenhava o
 *     cartão de ACEITE — que esconde o campo de e-mail e o próprio "Esqueci a senha".
 *     Quem chegasse por um convite vencido, pedisse recuperação e voltasse ficava
 *     preso num formulário sem saída, sem nunca ter feito nada errado.
 */
export function limparModoDaUrl(): void {
  const url = new URL(location.href)
  url.searchParams.delete('reset')
  url.searchParams.delete('convite')
  history.replaceState(null, '', url.toString())
}

function Moldura({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <main className="entrada">
      <div className="entrada-conteudo">
        {/* A logo em imagem, como no login: o fundo é claro, e o texto à mão era um quarto desenho da marca. */}
        <img className="marca-login" src="/marca/logo-digitalview-navy.png" alt="DigitalView" width={168} height={33} />
        {/* Mesma moldura do login: o rótulo do portal é o mesmo eyebrow, com a mesma caixa. */}
        <p className="portal-tipo eyebrow">PORTAL DE CLIENTES E PARCEIROS</p>
        <section className="login-card">
          <p className="eyebrow">ACESSO EXTERNO</p>
          <h1>{titulo}</h1>
          {children}
        </section>
        <p className="apoio">Não tem acesso? Fale com seu contato na DigitalView.</p>
      </div>
    </main>
  )
}

/** Pedir o link: um campo, e uma resposta que não diz se a conta existe. */
export function EsqueciSenha({ aoVoltar }: { aoVoltar: () => void }) {
  const [email, setEmail] = useState('')
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState('')
  const [ocupado, setOcupado] = useState(false)

  async function submeter(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro('')
    setOcupado(true)
    try {
      await enviar('auth/esqueci-senha', { email }, respostaNeutraDoPortal)
      setEnviado(true)
    } catch (falha) {
      /*
        A neutralidade protege UMA diferença: a que existe entre um e-mail cadastrado e
        um não cadastrado. Ela não cobre — e não deve cobrir — as falhas que não têm
        nada a ver com o endereço.

        A primeira versão deste `catch` transformava TODA falha que não fosse 429 em
        "enviamos o link". O efeito era a tela mentindo: o Wi-Fi caiu, ou o servidor
        respondeu 500, e a pessoa ia esperar um e-mail que ninguém chegou a pedir. Pior
        no caso do 400: `type="email"` do navegador aceita "joao@empresa" sem TLD, o
        `z.email()` do servidor não aceita, e a pessoa que errou de digitação recebia
        confirmação de envio.

        Nenhuma dessas três respostas depende de o e-mail existir, então nomeá-las não
        devolve nada a quem tenta enumerar.
      */
      if (falha instanceof FalhaDoPortal && falha.tipo === 'excesso') {
        setErro(falha.message)
      } else if (falha instanceof FalhaDoPortal && falha.tipo === 'requisicao') {
        setErro(falha.message === '' ? 'Confira o endereço digitado.' : falha.message)
      } else if (falha instanceof FalhaDoPortal && (falha.tipo === 'rede' || falha.tipo === 'servidor')) {
        setErro('Não foi possível enviar agora. Tente de novo em instantes.')
      } else {
        // Só aqui a confirmação neutra: a resposta veio, e o que ela não diz é
        // exatamente o que ela não deve dizer.
        setEnviado(true)
      }
    } finally {
      setOcupado(false)
    }
  }

  if (enviado) {
    return (
      <Moldura titulo="Verifique o seu e-mail">
        <p className="intro" data-teste="confirmacao-neutra">
          Se o e-mail estiver cadastrado, enviamos um link de recuperação.
        </p>
        <p className="nota">O link vale por 2 horas e só pode ser usado uma vez.</p>
        <button className="link" type="button" onClick={aoVoltar} data-teste="voltar-ao-login">
          <ArrowLeft size={14} /> Voltar para o acesso
        </button>
      </Moldura>
    )
  }

  return (
    <Moldura titulo="Recuperar acesso">
      <p className="intro">Informe o e-mail do seu acesso e enviaremos um link para criar uma senha nova.</p>
      {erro !== '' && (
        <div className="erro" role="alert">
          {erro}
        </div>
      )}
      <form
        onSubmit={(evento) => {
          void submeter(evento)
        }}
      >
        <label htmlFor="portal-recuperar-email">
          E-mail
          <input
            id="portal-recuperar-email"
            type="email"
            placeholder="voce@empresa.com"
            value={email}
            onChange={(evento) => {
              setEmail(evento.target.value)
            }}
            required
            autoComplete="email"
            data-teste="email-recuperacao"
          />
        </label>
        <button className="primario" disabled={ocupado} data-teste="enviar-link">
          {ocupado ? 'Enviando…' : 'Enviar link'}
          <ArrowRight size={16} />
        </button>
      </form>
      <button className="link" type="button" onClick={aoVoltar} data-teste="voltar-ao-login">
        <ArrowLeft size={14} /> Voltar para o acesso
      </button>
    </Moldura>
  )
}

/**
 * Definir a senha nova pelo link — e ENTRAR (spec 11.3, linha 20).
 *
 * Dois campos, como no convite: uma senha que só existe uma vez, num campo mascarado,
 * é a receita para a recuperação virar outra recuperação.
 */
export function RedefinirSenha({
  token,
  aoEntrar,
  aoPedirNovoLink,
}: {
  token: string
  aoEntrar: (eu: EuExternoDoPortal) => void
  aoPedirNovoLink: () => void
}) {
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [erro, setErro] = useState('')
  const [linkMorto, setLinkMorto] = useState(false)
  const [ocupado, setOcupado] = useState(false)

  async function submeter(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro('')
    if (senha.length < MINIMO_DA_SENHA) {
      setErro(`A senha precisa de ao menos ${String(MINIMO_DA_SENHA)} caracteres.`)
      return
    }
    if (senha !== confirmacao) {
      setErro('As duas senhas não são iguais.')
      return
    }
    setOcupado(true)
    try {
      const eu = await enviar('auth/redefinir-senha', { token, senha }, euExternoDoPortal)
      limparModoDaUrl()
      aoEntrar(eu)
    } catch (falha) {
      /*
        `ausente` e `contrato` não entram aqui: um link recusado volta como erro de
        requisição com a mensagem do servidor, que NOMEIA a causa (vencido, já usado,
        revogado). É essa mensagem que a pessoa precisa ler para saber que o caminho
        é pedir outro link, e não tentar de novo.
      */
      if (falha instanceof FalhaDoPortal && falha.tipo === 'excesso') {
        setErro('Muitas tentativas. Aguarde um minuto e tente de novo.')
      } else if (falha instanceof FalhaDoPortal && falha.tipo === 'requisicao') {
        setErro(falha.message)
        setLinkMorto(true)
      } else {
        // Pane, rede ou contrato: NÃO é o link. Dizer "link vencido" aqui mandaria a
        // pessoa pedir outro e-mail para um problema que outro e-mail não resolve.
        setErro('Não foi possível redefinir a senha agora. Tente de novo em instantes.')
      }
    } finally {
      setOcupado(false)
    }
  }

  if (linkMorto) {
    return (
      <Moldura titulo="Link inválido ou vencido">
        <p className="intro" data-teste="link-morto">
          {erro}
        </p>
        <button
          className="primario"
          type="button"
          data-teste="pedir-novo-link"
          onClick={() => {
            limparModoDaUrl()
            aoPedirNovoLink()
          }}
        >
          Pedir novo link
          <ArrowRight size={16} />
        </button>
      </Moldura>
    )
  }

  return (
    <Moldura titulo="Criar uma senha nova">
      <p className="intro">Escolha a senha que você vai usar para entrar no portal.</p>
      {erro !== '' && (
        <div className="erro" role="alert">
          {erro}
        </div>
      )}
      <form
        onSubmit={(evento) => {
          void submeter(evento)
        }}
      >
        <div className="grupo-campo">
          <span className="rotulo-senha">
            <label htmlFor="portal-nova-senha">Nova senha</label>
          </span>
          <span className="campo-senha">
            <input
              id="portal-nova-senha"
              type={mostrarSenha ? 'text' : 'password'}
              placeholder="••••••••"
              minLength={MINIMO_DA_SENHA}
              value={senha}
              onChange={(evento) => {
                setSenha(evento.target.value)
              }}
              required
              autoComplete="new-password"
              data-teste="nova-senha"
            />
            <button
              type="button"
              onClick={() => {
                setMostrarSenha((valor) => !valor)
              }}
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </span>
          <small className="ajuda-senha">Ao menos {MINIMO_DA_SENHA} caracteres.</small>
        </div>

        <label htmlFor="portal-nova-confirmacao">
          Confirmar senha
          <input
            id="portal-nova-confirmacao"
            type={mostrarSenha ? 'text' : 'password'}
            placeholder="••••••••"
            minLength={MINIMO_DA_SENHA}
            value={confirmacao}
            onChange={(evento) => {
              setConfirmacao(evento.target.value)
            }}
            required
            autoComplete="new-password"
            data-teste="confirmar-nova-senha"
          />
        </label>

        <button className="primario" disabled={ocupado} data-teste="salvar-nova-senha">
          {ocupado ? 'Salvando…' : 'Salvar e entrar'}
          <ArrowRight size={16} />
        </button>
      </form>
      <p className="nota">Ao salvar, as sessões abertas nesta conta são encerradas.</p>
    </Moldura>
  )
}
